/**
 * EPIC 45 — admin-web client for the leave request → approval workflow.
 *
 * All mutations go through Cloud Functions (requestLeave / reviewLeaveRequest /
 * cancelLeaveRequest — /leaveRequests is callable-write-only per
 * firestore.rules). Reads are live Firestore subscriptions scoped by the read
 * rule: a worker sees only their own requests (workerId == uid); staff see all.
 * Queries use a single `where` (no orderBy) and sort client-side so no
 * composite index is required.
 */

import type { LeaveRequest, LeaveType, ReviewLeaveRequestResult } from '@rendimees24/shared-types';
import {
  collection,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';

export interface RequestLeaveInput {
  /** Omitted → the caller (a worker) requests for themselves. */
  workerId?: string;
  type: LeaveType;
  from: string;
  to: string;
  reason?: string;
  /**
   * Stage 1A-d — one id per tap, reused on every retry and queued replay; the
   * server answers a repeat with the same request (`replayed: true`). The
   * worker UI goes through `requestLeaveQueued` (services/syncQueue), which
   * sets it.
   */
  clientRequestId?: string;
  /** The account that queued it — a replay on another account is refused. */
  expectedWorkerId?: string;
}

export async function requestLeave(
  input: RequestLeaveInput,
): Promise<{ ok: true; id: string; replayed?: true }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<RequestLeaveInput, { ok: true; id: string; replayed?: true }>(
    functions,
    'requestLeave',
  );
  const { data } = await callable(input);
  return data;
}

export interface ReviewLeaveRequestInput {
  requestId: string;
  decision: 'approve' | 'reject';
  note?: string;
  /** 0.4 — preview what approving would release; writes nothing. */
  dryRun?: boolean;
}

/**
 * Decide a request. Approving releases the person's shifts inside the
 * absence (0.4); the admin UI always shows the `dryRun` preview first and
 * approves (or re-approves an unfinished release) only from it.
 */
export async function reviewLeaveRequest(
  input: ReviewLeaveRequestInput,
): Promise<ReviewLeaveRequestResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<ReviewLeaveRequestInput, ReviewLeaveRequestResult>(
    functions,
    'reviewLeaveRequest',
  );
  return (await callable(input)).data;
}

export async function cancelLeaveRequest(requestId: string): Promise<void> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ requestId: string }, { ok: true }>(
    functions,
    'cancelLeaveRequest',
  );
  await callable({ requestId });
}

function byRequestedAtDesc(a: LeaveRequest, b: LeaveRequest): number {
  return (b.requestedAt ?? '').localeCompare(a.requestedAt ?? '');
}

/** A worker's own leave requests (all statuses), newest first. */
export function subscribeMyLeaveRequests(
  uid: string,
  onSnap: (rows: LeaveRequest[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    query(collection(firestore, 'leaveRequests'), where('workerId', '==', uid)),
    (snap: QuerySnapshot) => {
      onSnap(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as LeaveRequest)
          .sort(byRequestedAtDesc),
      );
    },
    (error) => onError?.(error),
  );
}

/**
 * 0.4 — approved requests whose staffing release is not finished (`partial`
 * or `needs_staff`): RM24 decides the rest, or re-approves from the preview.
 */
export function subscribeUnfinishedLeaveReleases(
  onSnap: (rows: LeaveRequest[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    query(
      collection(firestore, 'leaveRequests'),
      where('staffingRelease.status', 'in', ['partial', 'needs_staff']),
    ),
    (snap: QuerySnapshot) => {
      onSnap(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as LeaveRequest)
          // A cancelled request keeps its release record (reports read it).
          .filter((r) => r.status === 'approved')
          .sort(byRequestedAtDesc),
      );
    },
    (error) => onError?.(error),
  );
}

/** Every PENDING leave request (staff review queue), newest first. */
export function subscribePendingLeaveRequests(onSnap: (rows: LeaveRequest[]) => void): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    query(collection(firestore, 'leaveRequests'), where('status', '==', 'pending')),
    (snap: QuerySnapshot) => {
      onSnap(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as LeaveRequest)
          .sort(byRequestedAtDesc),
      );
    },
  );
}
